import React, { ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router";
import { V2 } from "../../Api";
import { deepCopy, diffObjects } from "../../Utilities";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import withTitle from "../withTitle";

interface IDataListViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    hasChanged: boolean;
    isDeleteDialogOpen: boolean;

    originalData: any;
    data: any;

    itemChanges: ItemChange<string>[];

    newItemName: string;
}

interface IDataListViewProps extends RouteComponentProps<any> {

}

type ItemChange<T> = {
    type: "add";
    data: T;
} | {
    type: "delete";
    data: T;
};

class DataListView extends React.Component<IDataListViewProps, IDataListViewState> {
    constructor(props: Readonly<IDataListViewProps>) {
        super(props);

        const state: IDataListViewState = {
            isLoading: true,
            isSubmitting: false,
            hasChanged: false,
            isDeleteDialogOpen: false,

            originalData: {},
            data: { items: [] },

            itemChanges: [],

            newItemName: ""
        };

        this.state = state;

        this.formInputChanged = this.formInputChanged.bind(this);
        this.newInputChanged = this.newInputChanged.bind(this);
        this.addItem = this.addItem.bind(this);
        this.submit = this.submit.bind(this);
        this.goBack = this.goBack.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.deleteList = this.deleteList.bind(this);
        this.handleDeleteSuccess = this.handleDeleteSuccess.bind(this);
    }

    public componentDidMount() {
        if (this.props.match.params.id) {
            this.loadData();
        }
    }

    public render() {
        const data = this.state.data;
        return (
            <div className="viewer-root input-form">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onRequestClose={this.toggleDeleteDialog} onConfirm={this.deleteList} onSuccess={this.handleDeleteSuccess} />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem><button type="button" onClick={this.toggleDeleteDialog}><i className="icon-trash" /><span>Delete list...</span></button></ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>List Info</h3>
                        </header>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-name">Name</label>
                                <input type="text" id="frm-name" name="name" value={data.name || ""} onChange={this.formInputChanged} />
                            </div>
                            <div className="input-form-group">
                                <label htmlFor="frm-resource-locator">Resource Locator</label>
                                <input type="text" id="frm-resource-locator" name="resourceLocator" value={data.resourceLocator || ""} onChange={this.formInputChanged} style={{ textTransform: "lowercase" }} />
                            </div>
                        </section>
                    </section>
                    <section className="spacer" />
                </div>
                &nbsp;
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.data?.items?.map((a: any, i: number) => <tr key={i}>
                            <td>{a.value}</td>
                            <td className="col-nopad-v"><button type="button" className="bare red icon-times" onClick={() => this.deleteItem(i)} /></td>
                        </tr>)}
                        <tr>
                            <td className="col-nopad">
                                <input type="text" name="newItemName" value={this.state.newItemName} onChange={this.newInputChanged} onKeyDown={e => e.key === "Enter" && this.addItem()} />
                            </td>
                            <td className="col-nopad">
                                <button type="button" className="button small btn-light narrow icon-plus" onClick={this.addItem} />
                            </td>
                        </tr>
                    </tbody>
                </table>
                {this.state.isSubmitting === true
                    ? <>&nbsp;<div className="spinner lds-dual-ring" /></>
                    : typeof this.state.isSubmitting === "string"
                        ? <>&nbsp;<div className="error-text">{this.state.isSubmitting}</div></>
                        : null}
                &nbsp;
                <div className="action-panel-dialog">
                    <button type="button" className="button" onClick={this.submit}>Save</button>
                    <button type="button" className="button" onClick={this.goBack}>Cancel</button>
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const list = await V2.get<any>(`list/${this.props.match.params.id}`);

            this.setState({
                isLoading: false,
                originalData: list,
                data: deepCopy(list)
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load list"
            });
        }
    }

    private newInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        let name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any);
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, converter?: (value: any) => any) {
        let name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        let data = this.state.data;
        if (name.includes(".")) {
            const segments = name.split(".");
            [name] = segments.splice(segments.length - 1, 1);

            for (const seg of segments) {
                if (!data[seg]) {
                    data[seg] = {};
                }
                data = data[seg];
            }
        }

        data[name] = converter ? converter(value) : value;

        this.setState(s => ({
            hasChanged: true,
            data: s.data
        }));
    }

    private deleteItem(index: number) {
        const data = this.state.data;
        const itemChanges = this.state.itemChanges;

        const [item] = data.items.splice(index, 1);

        const addIndex = itemChanges.findIndex(a => a.type === "add" && a.data === item.value);
        if (addIndex !== -1) {
            // Remove the add, as we're just deleting it again
            itemChanges.splice(addIndex, 1);
        }
        else {
            itemChanges.push({
                type: "delete",
                data: item.value
            });
        }

        this.setState({
            data,
            itemChanges,
            hasChanged: true
        });
    }

    private addItem() {
        const data = this.state.data;
        const itemChanges = this.state.itemChanges;

        data.items.push({
            value: this.state.newItemName
        });

        data.items.sort((a: any, b: any) => a.value.localeCompare(b));

        const existingDelete = itemChanges.findIndex(a => a.type === "delete" && a.data === this.state.newItemName);
        if (existingDelete !== -1) {
            // Remove delete change, as we've added the item back
            itemChanges.splice(existingDelete, 1);
        }
        else {
            itemChanges.push({
                type: "add",
                data: this.state.newItemName
            });
        }

        this.setState({
            data,
            itemChanges,
            newItemName: ""
        });
    }

    private submit() {
        // Add item just in case user forgot to push the plus
        if (this.state.newItemName)
            this.addItem();

        if (!this.props.match.params.id)
            return this.saveNew();
        else
            return this.save();
    }

    private async save() {
        try {
            this.setState({
                isSubmitting: true
            });

            const data: any = diffObjects(this.state.originalData, this.state.data);
            if (data) {
                delete data.items;
                data.additions = this.state.itemChanges.filter(a => a.type === "add").map(a => a.data);
                data.deletions = this.state.itemChanges.filter(a => a.type === "delete").map(a => a.data);

                await V2.patch(`list/${this.props.match.params.id}`, data);
            }

            this.setState({
                isSubmitting: false,
                hasChanged: false
            }, () => this.goBack());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save list"
            });
        }
    }

    private async saveNew() {
        try {
            this.setState({
                isSubmitting: true
            });

            const data: any = this.state.data;

            await V2.post(`list`, data);

            this.setState({
                isSubmitting: false,
                hasChanged: false
            }, () => this.goBack());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save list"
            });
        }
    }

    private goBack() {
        this.props.history.goBack();
    }

    private toggleDeleteDialog() {
        this.setState(s => ({
            isDeleteDialogOpen: !s.isDeleteDialogOpen
        }));
    }

    private handleDeleteSuccess() {
        this.setState({
            hasChanged: false
        }, () => this.goBack());
    }

    private async deleteList() {
        V2.delete(`list/${this.props.match.params.id}`);
    }
}

export default withTitle("Data Lists", DataListView);
